import React from "react";
import { Redirect, withRouter } from "react-router-dom";

import Video from "@components/video/VideoComponent";
import RegistrationForm from "@components/registrationForm/RegistrationFormComponent";
import FormTitle from "@components/registrationFormTitle/RegistrationFormTitleComponent";
import ConfirmationPopup from "@components/registrationConfirmationPopup/RegistrationConfirmationPopupComponent";

import "./RegistrationComponent.scss";
import logo from "@resources/images/conceiveAbilities-logo.svg";
import AppService from "@service/AppService/AppService";
import imgSurrogate from "@resources/images/GC_sign_in_image.png";
import LanguagePicker from "@components/languagePicker/LanguagePicker";
import { AuthContext } from "../../AuthContext";
import Footer from "../../components/Footer/Footer";
import Constants from "@helper/Constants";
import ReCaptchaScriptLoader from "../../components/ReCaptchaScriptLoader/ReCaptchaScriptLoader";
import { getRecaptchaToken } from "../../shared/utils/getRecaptchaToken";
import { getStoredCountryCode, resolveRegistrationCountryCode } from "@shared/utils/registrationUserLocation";

class RegistrationComponent extends React.Component {
    static contextType = AuthContext;
    registerErrorMessage = "An error occurred while try to process your request";
    constructor(props) {
        super(props);
        const milkDonorCached = props.page === "milk_donor" ? getStoredCountryCode() : null;
        this.state = {
            showConfirmationPopup: false,
            showError: false,
            showRegistrationError: false,
            showToggle: false,
            milkDonorCountryCode: milkDonorCached,
            milkDonorGeoReady: props.page !== "milk_donor" || milkDonorCached != null
        };
        this.saveProfile = this.saveProfile.bind(this);
        this.getTitle = this.getTitle.bind(this);
        this.setShowToggle = this.setShowToggle.bind(this);
    }

    TITLES = {
        egg_donor: "Register as an egg donor",
        parent: "Register as a parent",
        surrogate: "Start A Surrogacy Journey!",
        milk_donor: "Submit your Milk Donation Challenge proof of donation"
    };

    componentDidMount() {
        if (this.props.page === "surrogate") {
            AppService.changeLanguageHandler(AppService.cookies.get("i18next"), this.props.history);
        } else {
            // Egg donor, intended parent, milk donor: English-only on entry (research WP-971 §11).
            AppService.changeLanguageHandler("en", this.props.history);
        }

        if (this.props.page === "milk_donor" && !this.state.milkDonorGeoReady) {
            resolveRegistrationCountryCode().then((code) => {
                this.setState({
                    milkDonorCountryCode: code ?? "",
                    milkDonorGeoReady: true
                });
            });
        }
    }

    getTitle() {
        const title = this.TITLES[this.props.page];
        return this.props.page ? title : "please select";
    }

    async saveProfile(profile) {
        if (AppService.accountType.length === 0) {
            this.setState({ showError: "Please select profile type" });
            return;
        }

        this.setState({ showProgress: true, showError: false });

        const { firstName, lastName } = profile;

        const role = AppService.accountType.toUpperCase();

        const user = {
            firstName: firstName,
            lastName: lastName,
            email: profile.email,
            address: "",
            primaryPhoneNumber: "",
            role
        };
        if (this.props.page !== "milk_donor") {
            user.password = profile.password;
        }

        try {
            const reCaptchaToken = await getRecaptchaToken();

            if (!reCaptchaToken) {
                return;
            }

            const result = await this.context.signup({ ...user, reCaptchaToken });
            if (result) {
                let fn = null;
                switch (user.role) {
                    case "PARENT":
                        fn = AppService.sendIPRegistrationToPardot;
                        break;
                    case "SURROGATE":
                        fn = AppService.sendGCRegistrationToPardot;
                        break;
                    case "EGG_DONOR":
                        fn = AppService.sendEDRegistrationToPardot;
                        break;
                }
                if (fn) {
                    fn = fn.bind(AppService);
                    fn(firstName, lastName, profile.email);
                }
                this.props.history.push("/start");
            }
        } catch (error) {
            if (error.response?.status === 409) {
                // FR-008: milk-donor-only duplicate email should not be 409; avoid implying duplicate when role is milk donor.
                const duplicateMessage = "This email address already exists. Sign-in or use another email address.";
                this.setState({
                    showProgress: false,
                    showError: this.props.page === "milk_donor" ? this.registerErrorMessage : duplicateMessage
                });
            } else {
                this.setState({
                    showProgress: false,
                    showError: "An error occurred while try to process your request"
                });
            }
        }
    }

    setShowToggle(internationalized) {
        this.setState({ showToggle: internationalized });
        if (!internationalized) {
            AppService.changeLanguageHandler("en", this.props.history);
        }
    }

    render() {
        const title = this.getTitle();
        const page = this.props.page;
        const { showConfirmationPopup, email, navigateTo, user, showRegistrationError, showError, milkDonorCountryCode } = this.state;
        const submitOrServerError = typeof showError === "string" ? showError : "";
        const appValidationError = AppService.getValidationErrorText();
        const showFormError = submitOrServerError !== "" || appValidationError !== "" || showRegistrationError;
        const formErrorMessage = submitOrServerError || (appValidationError !== "" || showRegistrationError ? appValidationError : "");

        if (navigateTo) {
            return <Redirect to={`/survey/`} push={true} />;
        }

        AppService.accountType = page;

        return (
            <div className="row security">
                {this.state.showProgress ? <div className={`animated-gradient-${AppService.accountType}`} /> : null}
                <div className="column left-column">
                    <header className="left-column-header">
                        <a href={Constants.PORTAL_URL} target="_blank" rel="noopener noreferrer">
                            <img src={logo} className="app-logo" alt="ConceiveAbilities" />
                        </a>
                        {/* <img src={menu} className="app-menu" alt="Menu" /> */}
                        {AppService.accountType === "surrogate" && this.state.showToggle && AppService.lang !== "en" && (
                            <LanguagePicker history={this.props.history} />
                        )}
                    </header>
                    {page === "surrogate" ? (
                        <div className="surrogate-image-mobile">
                            <img src={imgSurrogate} alt="" />
                        </div>
                    ) : null}
                    <div className="registration-form">
                        <FormTitle
                            title={title}
                            titles={this.TITLES}
                            page={page}
                            titleDropDownClosed={() => this.setState({ showError: false })}
                        />
                        <RegistrationForm
                            onSubmit={this.saveProfile}
                            page={page}
                            showError={showFormError}
                            errorMessage={formErrorMessage}
                            getShowToggle={this.setShowToggle}
                            showProgress={this.state.showProgress}
                            milkDonorCountryCode={milkDonorCountryCode}
                        />
                    </div>
                    <Footer halfWidth isVisible />
                </div>
                <div tabIndex={0} className="column right-column video-column">
                    {!AppService.isMobile() ? <Video page={page} /> : null}
                </div>
                {showConfirmationPopup && (
                    <ConfirmationPopup
                        onDialogClose={() =>
                            this.setState({
                                showConfirmationPopup: false,
                                navigateTo: "/login"
                            })
                        }
                        title="Email confirmation"
                        navigateTo="start"
                        email={email}
                        user={user}
                    />
                )}
                <ReCaptchaScriptLoader />
            </div>
        );
    }
}

export default withRouter(RegistrationComponent);
